import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useI18nText } from '@/lib/utils/i18nText';
import { GgcPagination } from '@/components/ui/ggc/pagination';
import { ColumnHeaderMenu, type SortMenuOption } from '@/components/ColumnHeaderMenu';
import TagsHelpPopover from '@/components/TagsHelpPopover';
import { InfluencerRow } from './InfluencerRow';
import type { Influencer, Campaign, CampaignPost, KpiOption, ColumnDef, CpvEstimation } from '../types';

// Column ids that are not hideable from the header menu.
const NO_TOGGLE_IDS = ['name'];

const KPI_OPTION_LABELS: Record<KpiOption, string> = {
  freeChoice: 'Free Choice',
  allTime: 'All time',
  recent: 'Recent',
  thisCampaign: 'This camp.',
  byFilteredCampaigns: 'Filtered camp.',
};

/** One group section in "sections mode" (see `sections` below) — its own header row
 *  followed by its own rows, all inside the ONE shared table/scroll-wrap. */
export interface InfluencerTableSection {
  key: string;
  header: React.ReactNode;
  rows: Influencer[];
}

interface InfluencerTableProps {
  // data
  displayedInfluencers: Influencer[];
  influencers: Influencer[];                 // for select-all "all selected" check
  perUserAnalysis?: Record<string, { posts?: CampaignPost[] }>;
  cpvEstimationByUserId?: Record<string, CpvEstimation>;
  visibleCols: ColumnDef[];                  // headers + colgroup
  columns: ColumnDef[];                      // width lookup
  rowColumns: ColumnDef[];                   // columns handed to each InfluencerRow
  // selection
  selectedUserIds: Set<string>;
  onToggle: (id: string) => void;
  onSelectAll: (checked: boolean) => void;
  // sort
  sortBy: string;
  onSortChange: (sortBy: string) => void;
  // column ops (omitted by the drawer → no resize / hide)
  onHideColumn?: (colId: string) => void;
  onStartResize?: (colId: string, e: React.MouseEvent) => void;
  // pagination
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  pageSize: number;
  // row context
  isAdmin: boolean;
  campaigns: Campaign[];
  rowSelectedCampaignId?: string;
  organizationId?: string;
  kpiOption?: KpiOption;
  computeInCampaign: (u: Influencer) => boolean;
  computeInCampaignDeleted: (u: Influencer) => boolean;
  computeInCampaignRejected: (u: Influencer) => boolean;
  // row callbacks
  onOpenPanelDrawer?: (id: string) => void;
  onChatSystemDrawer?: (id: string) => void;
  onChannelDetailsDrawer?: (id: string) => void;
  onStatsDrawer?: (id: string) => void;
  onChannelEditDrawer?: (id: string) => void | Promise<void>;
  onTagsSaved?: (userId: string, newTags: string[]) => void;
  onTagRemove?: (u: Influencer, tag: string) => Promise<void>;
  // sibling expand
  expandedSiblingParents: Set<string>;
  onSiblingToggle: (userId: string, expanded: boolean) => void;
  // view flags
  shortSubsFormat?: boolean;
  showEmptyDash?: boolean;
  showEmptyMetricIcons?: boolean;
  alwaysShowButtons?: boolean;
  showChannelHandle?: boolean;
  showColumnUpdatedAt?: boolean;
  // KPI source picker (main page only; drawer passes false)
  showKpiSourcePicker?: boolean;
  setKpiOption?: (o: KpiOption) => void;
  // empty-state slot (main page passes its "no match → Add" row)
  emptyState?: React.ReactNode;
  /** Grouped-sections mode (the campaign dashboard's "Group by candidate group" view) —
   *  when provided, renders one <tbody> per section (its own header <tr> + that
   *  section's rows) inside this ONE shared table/scroll-wrap/thead, instead of the
   *  single flat <tbody> below. `displayedInfluencers`/page/totalPages/onPageChange are
   *  ignored in this mode (no pagination — every section shows all of its rows). */
  sections?: InfluencerTableSection[];
}

/** Click-to-toggle info popover for a table header cell (the "#" legend and the
 *  status-icon-column guide) — replaces the previous CSS `:hover` reveal, which
 *  had no way to stay open for reading/scrolling and no touch-device equivalent.
 *  Closes on an outside click; doesn't portal (the header cells it's used in
 *  already carry an `overflow: visible` override so the popover isn't clipped
 *  by the table's own scroll wrap). */
function HeaderHelpTrigger({
  trigger, popoverClassName, children,
}: {
  trigger: React.ReactNode;
  popoverClassName: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  return (
    <>
      <span
        ref={triggerRef}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
      >
        {trigger}
      </span>
      {open && (
        <div ref={popoverRef} className={popoverClassName}>
          {children}
        </div>
      )}
    </>
  );
}

/**
 * Shared influencer dashboard table — the round-card scrollable table with the
 * fixed `#`/checkbox/status-icon columns, sortable/hideable/resizable column
 * headers, `InfluencerRow` body, custom horizontal scrollbar and pagination.
 *
 * Purely controlled: all state arrives via props, all events leave via callbacks.
 * It owns only the horizontal custom-scrollbar DOM (refs + the scrollable flag),
 * which is local to its own markup. Used by both the campaign dashboard
 * (`DashboardContent`) and the "add influencers" search drawer.
 */
export function InfluencerTable({
  displayedInfluencers, influencers, perUserAnalysis, cpvEstimationByUserId,
  visibleCols, columns, rowColumns,
  selectedUserIds, onToggle, onSelectAll,
  sortBy, onSortChange,
  onHideColumn, onStartResize,
  page, totalPages, onPageChange, pageSize,
  isAdmin, campaigns, rowSelectedCampaignId, organizationId, kpiOption,
  computeInCampaign, computeInCampaignDeleted, computeInCampaignRejected,
  onOpenPanelDrawer, onChatSystemDrawer, onChannelDetailsDrawer, onStatsDrawer, onChannelEditDrawer,
  onTagsSaved, onTagRemove,
  expandedSiblingParents, onSiblingToggle,
  shortSubsFormat, showEmptyDash, showEmptyMetricIcons,
  alwaysShowButtons, showChannelHandle, showColumnUpdatedAt, showKpiSourcePicker, setKpiOption,
  emptyState,
  sections,
}: InfluencerTableProps) {
  const { t } = useI18nText();
  const tableWrapRef = useRef<HTMLDivElement>(null);
  const [tableScrollable, setTableScrollable] = useState(false);
  const customScrollbarRef = useRef<HTMLDivElement>(null);
  const customScrollbarThumbRef = useRef<HTMLDivElement>(null);
  const nSelected = selectedUserIds.size;

  useLayoutEffect(() => {
    const wrap = tableWrapRef.current;
    const bar = customScrollbarRef.current;
    const thumb = customScrollbarThumbRef.current;
    if (!wrap || !bar || !thumb) return;

    const updateThumb = () => {
      const ratio = wrap.clientWidth / wrap.scrollWidth;
      setTableScrollable(ratio < 1);
      if (ratio >= 1) { bar.style.display = 'none'; return; }
      bar.style.display = 'block';
      const thumbWidth = Math.max(bar.clientWidth * ratio, 30);
      const maxThumbLeft = bar.clientWidth - thumbWidth;
      const maxScrollLeft = wrap.scrollWidth - wrap.clientWidth;
      const thumbLeft = maxScrollLeft > 0 ? (wrap.scrollLeft / maxScrollLeft) * maxThumbLeft : 0;
      thumb.style.width = `${thumbWidth}px`;
      thumb.style.transform = `translateX(${thumbLeft}px)`;
    };

    let isDragging = false;
    let startX = 0;
    let startScrollLeft = 0;

    const onThumbMouseDown = (e: MouseEvent) => {
      isDragging = true;
      startX = e.clientX;
      startScrollLeft = wrap.scrollLeft;
      e.preventDefault();
    };
    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging || !customScrollbarRef.current || !customScrollbarThumbRef.current) return;
      const maxScrollLeft = wrap.scrollWidth - wrap.clientWidth;
      const maxThumbLeft = customScrollbarRef.current.clientWidth - parseFloat(customScrollbarThumbRef.current.style.width);
      wrap.scrollLeft = startScrollLeft + ((e.clientX - startX) / maxThumbLeft) * maxScrollLeft;
    };
    const onMouseUp = () => { isDragging = false; };

    // Wheel deltaX now scrolls natively since overflow-x is 'auto' (no manual
    // scrollLeft forwarding needed here) — the native scroll fires the
    // 'scroll' listener below, keeping the custom thumb in sync.

    wrap.addEventListener('scroll', updateThumb);
    thumb.addEventListener('mousedown', onThumbMouseDown);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    updateThumb();

    const ro = new ResizeObserver(updateThumb);
    ro.observe(wrap);

    return () => {
      wrap.removeEventListener('scroll', updateThumb);
      thumb.removeEventListener('mousedown', onThumbMouseDown);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      ro.disconnect();
    };
  }, [displayedInfluencers, visibleCols, sections]);

  // Mirrors the <colgroup> below exactly (rowNum? + checkbox + status-icon + every
  // visible non-rowNum column) — used as the section header <td>'s colSpan in
  // sections mode, so it always spans the full row width regardless of which
  // columns happen to be visible.
  const totalColCount =
    (visibleCols.some((c) => c.id === 'rowNum') ? 1 : 0) +
    1 + // checkbox
    1 + // status-icon legend
    visibleCols.filter((c) => c.id !== 'rowNum').length;

  // Shared by both the flat <tbody> and each section's <tbody> below — `rowIndex`
  // is the only thing that differs (continues across the current page in flat mode,
  // restarts at 1 within each section in sections mode).
  const renderRow = (user: Influencer, rowIndex: number) => (
    <InfluencerRow
      key={user._id}
      user={user}
      isAdmin={isAdmin}
      selected={selectedUserIds.has(user._id)}
      onToggle={onToggle}
      columns={rowColumns}
      inCampaign={computeInCampaign(user)}
      inCampaignDeleted={computeInCampaignDeleted(user)}
      inCampaignRejected={computeInCampaignRejected(user)}
      selectedUserIds={selectedUserIds}
      rowIndex={rowIndex}
      campaigns={campaigns}
      selectedCampaignId={rowSelectedCampaignId}
      organizationId={organizationId}
      kpiOption={kpiOption}
      onTagsSaved={onTagsSaved}
      onTagRemove={onTagRemove}
      shortSubsFormat={shortSubsFormat}
      showEmptyDash={showEmptyDash}
      showEmptyMetricIcons={showEmptyMetricIcons}
      showChannelHandle={showChannelHandle}
      showColumnUpdatedAt={showColumnUpdatedAt}
      onOpenPanelDrawer={onOpenPanelDrawer}
      onChatSystemDrawer={onChatSystemDrawer}
      onChannelDetailsDrawer={onChannelDetailsDrawer}
      onStatsDrawer={onStatsDrawer}
      onChannelEditDrawer={onChannelEditDrawer}
      perUserAnalysis={perUserAnalysis}
      cpvEstimationByUserId={cpvEstimationByUserId}
      siblingsExpanded={expandedSiblingParents.has(user._id)}
      onSiblingToggle={onSiblingToggle}
    />
  );

  return (
    <>
      <div ref={tableWrapRef} className={`dashboard-table-wrap${tableScrollable ? ' dashboard-table-wrap--scrolling' : ''}`}>
        <table
          className={`dashboard-table${alwaysShowButtons ? ' always-show-btns' : ''}`}
          style={{
            tableLayout: 'fixed',
            // Set explicit width = sum of columns so table-layout:fixed doesn't
            // distribute extra space when the table is wider than its columns.
            width: visibleCols.reduce((s, c) => s + (c.width ?? 100), 0) + 65,
            minWidth: '100%',
          }}
        >
          <colgroup>
            {/* Must mirror the exact th order:
                1. rowNum (conditional, 40px)
                2. checkbox-actions-cell
                3. status-icon-legend (25px)
                4. all visible non-rowNum cols */}
            {visibleCols.some((c) => c.id === 'rowNum') && <col style={{ width: 40 }} />}
            <col style={{ width: 30 }} />{/* checkbox */}
            <col style={{ width: 25 }} />{/* status-icon legend */}
            {visibleCols.filter((c) => c.id !== 'rowNum').map((c) => (
              <col key={c.id} style={{ width: c.width }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {visibleCols.some((c) => c.id === 'rowNum') && (
                <th key="rowNum" data-colid="rowNum" className="rownum-th w-[40px] !text-center">
                  <HeaderHelpTrigger
                    trigger={<>#<span className="status-icon-legend-trigger rownum-legend-trigger">?</span></>}
                    popoverClassName="rownum-legend-popover"
                  >
                    <div className="rownum-legend-popover__title">{t('Legend', '범례')}</div>
                    <ul className="rownum-legend-popover__list">
                      <li><span className="legend-swatch legend-swatch--yellow" /><i className="fa fa-clock rownum-status-icon rownum-status-icon--stripe-yellow" />{t("Latest influencer's video dated more than a month ago", '인플루언서의 최신 영상이 1개월 이상 지남')}</li>
                      <li><span className="legend-swatch legend-swatch--gray" /><i className="fa fa-clock rownum-status-icon rownum-status-icon--stripe-gray" />{t("Latest influencer's video dated more than a month ago (stats recently updated)", '인플루언서의 최신 영상이 1개월 이상 지남 (통계는 최근 갱신됨)')}</li>
                      <li><span className="legend-swatch legend-swatch--green" />{t('Influencer is in selected campaign', '인플루언서가 선택된 캠페인에 포함됨')}</li>
                      <li><span className="legend-swatch legend-swatch--red" /><i className="fa fa-frown-o rownum-status-icon rownum-status-icon--rejected" /><i className="fa fa-trash rownum-status-icon rownum-status-icon--deleted" />{t('Influencer is rejected or deleted from selected campaign', '인플루언서가 선택된 캠페인에서 거절 또는 삭제됨')}</li>
                      <li><span className="legend-swatch legend-swatch--blacklisted" /><i className="fa fa-ban rownum-status-icon rownum-status-icon--blacklisted" />{t('Blacklisted influencer', '블랙리스트 인플루언서')}</li>
                    </ul>
                  </HeaderHelpTrigger>
                </th>
              )}
              <th className="checkbox-actions-cell checkbox-actions-cell--no-actions">
                <div className="checkbox-actions-cell-inner">
                  <input type="checkbox" checked={nSelected > 0 && nSelected === influencers.length} onChange={(e) => onSelectAll(e.target.checked)} />
                </div>
              </th>
              <th className="status-icon-legend-th w-[25px]">
                <HeaderHelpTrigger
                  trigger={<span className="status-icon-legend-trigger">?</span>}
                  popoverClassName="status-icon-legend-popover"
                >
                  <div className="status-icon-legend-popover__title">{t('Status Icons Guide', '상태 아이콘 안내')}</div>
                  <ul className="status-icon-legend-popover__list">
                    <li>
                      <i className="fa fa-user-circle-o status-icon-legend-popover__icon" style={{ color: 'var(--ggc-green)' }} />
                      <span>{t('Signed up to GGContent — YouTube OAuth valid (auto stats collection active)', 'GGContent 가입 완료 — YouTube OAuth 인증 정상 (통계 자동 수집 중)')}</span>
                    </li>
                    <li>
                      <i className="fa fa-user-circle-o status-icon-legend-popover__icon" style={{ color: 'var(--ggc-red)' }} />
                      <span>{t('Signed up but YouTube OAuth error (expired or revoked — auto stats collection unavailable)', 'GGContent 가입했으나 YouTube OAuth 오류 (만료 또는 권한 취소 — 통계 자동 수집 불가)')}</span>
                    </li>
                    <li className="status-icon-legend-popover__section">{t('Campaign mode', '캠페인 모드')}</li>
                    <li>
                      <i className="fa fa-envelope status-icon-legend-popover__icon" style={{ color: 'var(--ggc-orange)' }} />
                      <span>{t('Influencer replied — unread by admin', '인플루언서 답장 — 관리자 미열람')}</span>
                    </li>
                    <li>
                      <i className="fa fa-envelope-open status-icon-legend-popover__icon" style={{ color: 'var(--ggc-green)' }} />
                      <span>{t('Email opened or influencer replied (read)', '이메일 오픈됨 또는 인플루언서 답장 확인')}</span>
                    </li>
                    <li>
                      <i className="fa fa-envelope status-icon-legend-popover__icon" style={{ color: '#607d8b' }} />
                      <span>{t('Email sent but not opened yet', '이메일 발송됨 — 미오픈')}</span>
                    </li>
                    <li className="status-icon-legend-popover__section">{t('All campaigns mode', '전체 모드')}</li>
                    <li>
                      <i className="fa fa-envelope status-icon-legend-popover__icon" style={{ color: '#860000' }} />
                      <span>{t('Never read any message', '메시지를 한 번도 읽지 않음')}</span>
                    </li>
                  </ul>
                </HeaderHelpTrigger>
              </th>
              {visibleCols.filter((c) => c.id !== 'rowNum').map((c) => {
                if (c.id === 'tags' && !isAdmin) return null;
                const canHide = !!onHideColumn && !NO_TOGGLE_IDS.includes(c.id);
                const align = c.id === 'subs' || c.id === 'rowNum' || c.id === 'cpvLast5' || c.id === 'cpvLastCampaign' ? 'right' : c.id === 'country' || c.id === 'workOrder' ? 'center' : undefined;
                // Use column width from state (resizable); fall back to legacy fixed widths
                const colFromState = columns.find((col) => col.id === c.id);
                const width = colFromState?.width ?? (c.id === 'rowNum' ? 40 : c.id === 'kpi' ? 140 : c.id === 'name' ? 170 : undefined);
                const onHide = canHide ? () => onHideColumn?.(c.id) : undefined;
                const alignClass = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : undefined;

                if (c.id === 'kpi') {
                  const kpiSortOptions: SortMenuOption[] = [
                    { value: 'kpiPricePerView_asc',   label: '$/view',  dir: 'asc',  metric: 'view' },
                    { value: 'kpiPricePerView_desc',  label: '$/view',  dir: 'desc', metric: 'view' },
                    { value: 'kpiPricePerClick_asc',  label: '$/click', dir: 'asc',  metric: 'click' },
                    { value: 'kpiPricePerClick_desc', label: '$/click', dir: 'desc', metric: 'click' },
                  ];
                  return (
                    <ColumnHeaderMenu
                      key={c.id}
                      dataColid={c.id}
                      title={c.label}
                      sortOptions={kpiSortOptions}
                      sortBy={sortBy}
                      onSortChange={onSortChange}
                      onHide={onHide}
                      className="text-center"
                      style={{ width }}
                      onStartResize={onStartResize}
                      extraSectionTitle={showKpiSourcePicker ? t('KPI source', 'KPI 출처') : undefined}
                      extraSection={
                        showKpiSourcePicker ? (
                          <div className="kpi-option-section">
                            {(Object.keys(KPI_OPTION_LABELS) as KpiOption[]).map((opt) => (
                              <label key={opt} className="kpi-option-section__radio">
                                <input type="radio" checked={kpiOption === opt} onChange={() => setKpiOption?.(opt)} />
                                <span>{KPI_OPTION_LABELS[opt]}</span>
                              </label>
                            ))}
                          </div>
                        ) : null
                      }
                    >
                      <span title={t('Based on videos created for our campaigns', '캠페인 영상 기준')}>{c.label}</span>
                    </ColumnHeaderMenu>
                  );
                }
                if (c.id === 'eng5') {
                  const engSortOptions: SortMenuOption[] = [
                    { value: 'engagementViewersToSubscribersPercent_asc',  label: t('Views/Subs', '조회/구독자'), dir: 'asc',  metric: 'viewsSubs' },
                    { value: 'engagementViewersToSubscribersPercent_desc', label: t('Views/Subs', '조회/구독자'), dir: 'desc', metric: 'viewsSubs' },
                    { value: 'engagementLikesPercent_asc',                 label: t('Likes %', '좋아요 %'),       dir: 'asc',  metric: 'likes' },
                    { value: 'engagementLikesPercent_desc',                label: t('Likes %', '좋아요 %'),       dir: 'desc', metric: 'likes' },
                    { value: 'engagementCommentPercent_asc',               label: t('Comments %', '댓글 %'),      dir: 'asc',  metric: 'comments' },
                    { value: 'engagementCommentPercent_desc',              label: t('Comments %', '댓글 %'),      dir: 'desc', metric: 'comments' },
                  ];
                  return (
                    <ColumnHeaderMenu
                      key={c.id}
                      dataColid={c.id}
                      title={c.label}
                      sortOptions={engSortOptions}
                      sortBy={sortBy}
                      onSortChange={onSortChange}
                      onHide={onHide}
                      style={{ width }}
                      onStartResize={onStartResize}
                    >
                      {c.label}
                    </ColumnHeaderMenu>
                  );
                }
                if (c.id === 'lva') {
                  const opts: SortMenuOption[] = [
                    { value: 'lastVideoActivity_asc',  label: t('Last video', '최근 영상'), dir: 'asc',  metric: 'lva' },
                    { value: 'lastVideoActivity_desc', label: t('Last video', '최근 영상'), dir: 'desc', metric: 'lva' },
                  ];
                  return (
                    <ColumnHeaderMenu key={c.id} dataColid={c.id} title={c.label} sortOptions={opts} sortBy={sortBy} onSortChange={onSortChange} onHide={onHide} style={{ width }} onStartResize={onStartResize}>
                      {c.label}
                    </ColumnHeaderMenu>
                  );
                }
                if (c.id === 'name') {
                  const opts: SortMenuOption[] = [
                    { value: 'name_asc',  label: t('Name', '이름'), dir: 'asc',  metric: 'name' },
                    { value: 'name_desc', label: t('Name', '이름'), dir: 'desc', metric: 'name' },
                  ];
                  return (
                    <ColumnHeaderMenu key={c.id} dataColid={c.id} title={c.label} sortOptions={opts} sortBy={sortBy} onSortChange={onSortChange} style={{ width }} onStartResize={onStartResize}>
                      {c.label}
                    </ColumnHeaderMenu>
                  );
                }
                if (c.id === 'subs') {
                  const opts: SortMenuOption[] = [
                    { value: 'subscribers_asc',  label: t('Subscribers', '구독자'), dir: 'asc',  metric: 'subs' },
                    { value: 'subscribers_desc', label: t('Subscribers', '구독자'), dir: 'desc', metric: 'subs' },
                  ];
                  return (
                    <ColumnHeaderMenu key={c.id} dataColid={c.id} title={c.label} sortOptions={opts} sortBy={sortBy} onSortChange={onSortChange} onHide={onHide} className="text-right" style={{ width }} onStartResize={onStartResize}>
                      {c.label}
                    </ColumnHeaderMenu>
                  );
                }
                if (c.id === 'country') {
                  const opts: SortMenuOption[] = [
                    { value: 'country_asc',  label: t('Country', '국가'), dir: 'asc',  metric: 'country' },
                    { value: 'country_desc', label: t('Country', '국가'), dir: 'desc', metric: 'country' },
                  ];
                  return (
                    <ColumnHeaderMenu key={c.id} dataColid={c.id} title={c.label} sortOptions={opts} sortBy={sortBy} onSortChange={onSortChange} onHide={onHide} className="text-center" style={{ width }} onStartResize={onStartResize}>
                      {c.label}
                    </ColumnHeaderMenu>
                  );
                }
                if (c.id === 'rav5') {
                  const opts: SortMenuOption[] = [
                    { value: 'averageViews_asc',  label: t('Avg. views', '평균 조회수'), dir: 'asc',  metric: 'rav5' },
                    { value: 'averageViews_desc', label: t('Avg. views', '평균 조회수'), dir: 'desc', metric: 'rav5' },
                  ];
                  return (
                    <ColumnHeaderMenu key={c.id} dataColid={c.id} title={c.label} sortOptions={opts} sortBy={sortBy} onSortChange={onSortChange} onHide={onHide} className="text-right" style={{ width }} onStartResize={onStartResize}>
                      {c.label}
                    </ColumnHeaderMenu>
                  );
                }
                if (c.id === 'estPrice') {
                  const opts: SortMenuOption[] = [
                    { value: 'lowestPrice_asc',   label: t('Lowest price', '최저가'),  dir: 'asc',  metric: 'lowest' },
                    { value: 'lowestPrice_desc',  label: t('Lowest price', '최저가'),  dir: 'desc', metric: 'lowest' },
                    { value: 'highestPrice_asc',  label: t('Highest price', '최고가'), dir: 'asc',  metric: 'highest' },
                    { value: 'highestPrice_desc', label: t('Highest price', '최고가'), dir: 'desc', metric: 'highest' },
                  ];
                  return (
                    <ColumnHeaderMenu key={c.id} dataColid={c.id} title={c.label} sortOptions={opts} sortBy={sortBy} onSortChange={onSortChange} onHide={onHide} className="text-right" style={{ width }} onStartResize={onStartResize}>
                      <i className="fa fa-usd text-ggc-text-gray text-xs mr-1" />{c.label}
                    </ColumnHeaderMenu>
                  );
                }
                if (c.id === 'cpvLast5') {
                  const opts: SortMenuOption[] = [
                    { value: 'cpvLast5_asc',  label: c.label, dir: 'asc',  metric: 'cpvLast5' },
                    { value: 'cpvLast5_desc', label: c.label, dir: 'desc', metric: 'cpvLast5' },
                  ];
                  return (
                    <ColumnHeaderMenu key={c.id} dataColid={c.id} title={c.label} sortOptions={opts} sortBy={sortBy} onSortChange={onSortChange} onHide={onHide} className="text-right" style={{ width }} onStartResize={onStartResize}>
                      {c.label}
                    </ColumnHeaderMenu>
                  );
                }
                if (c.id === 'cpvLastCampaign') {
                  const opts: SortMenuOption[] = [
                    { value: 'cpvLastCampaign_asc',  label: c.label, dir: 'asc',  metric: 'cpvLastCampaign' },
                    { value: 'cpvLastCampaign_desc', label: c.label, dir: 'desc', metric: 'cpvLastCampaign' },
                  ];
                  return (
                    <ColumnHeaderMenu key={c.id} dataColid={c.id} title={c.label} sortOptions={opts} sortBy={sortBy} onSortChange={onSortChange} onHide={onHide} className="text-right" style={{ width }} onStartResize={onStartResize}>
                      {c.label}
                    </ColumnHeaderMenu>
                  );
                }
                if (c.id === 'tags') {
                  return (
                    <ColumnHeaderMenu key={c.id} dataColid={c.id} title={c.label} onHide={onHide} style={{ width }} onStartResize={onStartResize}>
                      {c.label}<TagsHelpPopover />
                    </ColumnHeaderMenu>
                  );
                }
                return (
                  <ColumnHeaderMenu key={c.id} dataColid={c.id} title={c.label} onHide={onHide} className={alignClass} style={{ width }} onStartResize={onStartResize}>
                    {c.label}
                  </ColumnHeaderMenu>
                );
              })}
            </tr>
          </thead>
          {sections ? (
            sections.map((section) => (
              <tbody key={section.key}>
                {/* Reuses .candidates-group-header (styles/pages.css) — same group-section
                    header look as the Candidates page's own GroupRows. */}
                <tr className="candidates-group-header">
                  <td colSpan={totalColCount} className="px-4 py-2">{section.header}</td>
                </tr>
                {section.rows.map((user, idx) => renderRow(user, idx + 1))}
              </tbody>
            ))
          ) : (
            <tbody>
              {displayedInfluencers.length === 0 && emptyState}
              {displayedInfluencers.map((user, idx) => renderRow(user, (page - 1) * pageSize + idx + 1))}
            </tbody>
          )}
        </table>
      </div>
      <div className="dashboard-custom-scrollbar" ref={customScrollbarRef}>
        <div className="dashboard-custom-scrollbar-thumb" ref={customScrollbarThumbRef} />
      </div>
      {!sections && (
      <div className="flex justify-center mt-1">
        <GgcPagination
          page={page}
          totalPages={totalPages}
          onPageChange={onPageChange}
          labels={{
            prev: t('Previous page', '이전 페이지'),
            next: t('Next page', '다음 페이지'),
          }}
        />
      </div>
      )}
    </>
  );
}
